import { ErrorHandler, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';

import {
  DprAccumulationSection,
  DprPreviousValuesSet,
} from '@dpr-details/models';
import {
  compareDprOrder,
  createEmptyDprStatusTracker,
  getSectionsWithoutPreviousValues,
  getStatusTrackerPreviousValueColumnIds,
  getTableOfColumnsPreviousValueItemIds,
  isDprDirectlyBefore,
} from '@dpr-details/utils';
import { ProjectSetup } from '@shared/models';
import { DprDetails } from '@shared/models/dpr';
import { ToastNotificationService } from '@shared/services';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { DprCarryOverFormulaCalculationService } from '../accumulation';

/**
 * Keeps the previous values of the DPRs held on the device in step with the edits
 * made on it, the way the Personnel, Consumables and HSE offline services keep their
 * previous figures: every DPR carries its own starting point, and an edit is pushed
 * forward through the DPRs after it.
 */
@UntilDestroy()
@Injectable()
export class DprAccumulationOfflineService {
  constructor(
    private dprCarryOverFormulaCalculationService: DprCarryOverFormulaCalculationService,
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade,
    private errorHandler: ErrorHandler,
    private toastNotificationService: ToastNotificationService
  ) {}

  /**
   * Rebuilds the previous values of every cached DPR after the edited one, each from
   * the DPR before it once that one is calculated, and writes them back. The edited
   * DPR is passed in rather than read from the store, because its section only
   * reaches the store once the save that calls this has finished.
   *
   * Only the cached list is walked: it holds the latest DPRs with none missing in
   * between. A DPR opened outside it is walked from only when it comes directly
   * before the list; further back, DPRs between it and the list are not on the
   * device, so the list is left as it is, and the user is warned when the saved
   * section carries values over.
   */
  updateDependentPreviousValues(
    editedDprDetails: DprDetails,
    savedSection?: DprAccumulationSection
  ): Observable<boolean> {
    return combineLatest([
      this.userDetailsFacade.query.projectSetup$,
      this.offlineFacade.query.dprDetailsList$,
    ]).pipe(
      untilDestroyed(this),
      take(1),
      map(([projectSetup, dprDetailsList]) =>
        this.calculateLaterDprs(
          projectSetup,
          dprDetailsList,
          editedDprDetails,
          savedSection
        )
      ),
      // Every DPR is calculated before any is written, so a walk that fails
      // part-way leaves all of them as they were.
      tap((updatedDprs) => {
        if (updatedDprs) {
          updatedDprs.forEach((dpr) => this.offlineFacade.setDprDetails(dpr));
        } else {
          this.warnLaterDprsNotUpdated();
        }
      }),
      map((updatedDprs) => !!updatedDprs),
      // A walk that fails must not fail the save that started it.
      catchError((error) => {
        this.errorHandler.handleError(error);
        this.warnLaterDprsNotUpdated();

        return of(false);
      })
    );
  }

  /**
   * A copy of the DPR with the previous values it starts from: how the DPR before it
   * ends, once that one's carry-overs and formulas are calculated. With no DPR
   * before it, the previous values are null, as the API sends for a first DPR. If
   * the DPR before it can't be calculated, the DPR is returned without them.
   */
  withPreviousValues(
    projectSetup: ProjectSetup,
    dprDetails: DprDetails,
    previousDprDetails: DprDetails | null
  ): DprDetails {
    if (!this.hasPreviousValues(projectSetup)) {
      return dprDetails;
    }

    try {
      return this.withPreviousValuesFrom(
        projectSetup,
        dprDetails,
        this.buildPreviousValues(
          projectSetup,
          previousDprDetails
            ? this.dprCarryOverFormulaCalculationService.calculate(
                projectSetup,
                previousDprDetails
              )
            : null
        )
      );
    } catch (error) {
      this.errorHandler.handleError(error);

      return dprDetails;
    }
  }

  //#region Private methods

  /**
   * Every cached DPR after the edited one with its previous values rebuilt, in
   * date order, each from the DPR before it once that one is calculated. Null when
   * DPRs not on the device sit between the edited DPR and the cached list, and the
   * saved section carries values over.
   */
  private calculateLaterDprs(
    projectSetup: ProjectSetup,
    dprDetailsList: DprDetails[],
    editedDprDetails: DprDetails,
    savedSection: DprAccumulationSection | undefined
  ): DprDetails[] | null {
    if (!this.hasPreviousValues(projectSetup) || !dprDetailsList?.length) {
      return [];
    }

    if (
      !dprDetailsList.some((dpr) => dpr.id === editedDprDetails.id) &&
      !isDprDirectlyBefore(editedDprDetails, dprDetailsList)
    ) {
      const isBeforeList = dprDetailsList.some(
        (dpr) => compareDprOrder(editedDprDetails, dpr) < 0
      );

      return isBeforeList &&
        this.sectionHasPreviousValues(projectSetup, savedSection)
        ? null
        : [];
    }

    // Two DPRs created on the device one after the other can get the same date,
    // so the display id breaks the tie. The store's copy of the edited DPR from
    // before the save is left out by id, as its date can be written differently.
    const laterDprs = dprDetailsList
      .filter(
        (dpr) =>
          dpr.id !== editedDprDetails.id &&
          compareDprOrder(dpr, editedDprDetails) > 0
      )
      .sort((a, b) => compareDprOrder(a, b));

    if (!laterDprs.length) {
      return [];
    }

    let calculatedDpr = this.dprCarryOverFormulaCalculationService.calculate(
      projectSetup,
      editedDprDetails
    );

    return laterDprs.map((dpr, index) => {
      const previousValues = this.buildPreviousValues(
        projectSetup,
        calculatedDpr
      );
      const updatedDpr = this.withPreviousValuesFrom(
        projectSetup,
        dpr,
        previousValues
      );

      if (index < laterDprs.length - 1) {
        calculatedDpr = this.dprCarryOverFormulaCalculationService.calculate(
          projectSetup,
          updatedDpr,
          previousValues
        );
      }

      return updatedDpr;
    });
  }

  /**
   * A copy of the DPR with the given previous values. Only the sections it writes
   * to are copied, so the DPR held in the store is left untouched. A DPR nobody
   * saved the Status Tracker on gets the empty section the API sends for one, so
   * it has somewhere to keep the Status Tracker's previous values.
   */
  private withPreviousValuesFrom(
    projectSetup: ProjectSetup,
    dprDetails: DprDetails,
    previousValues: DprPreviousValuesSet
  ): DprDetails {
    const updatedDpr: DprDetails = {
      ...dprDetails,
      dprStatusTracker: dprDetails.dprStatusTracker && {
        ...dprDetails.dprStatusTracker,
      },
      dprTableOfColumns: dprDetails.dprTableOfColumns?.map(
        (dprTableOfColumn) => ({ ...dprTableOfColumn })
      ),
    };

    if (
      !updatedDpr.dprStatusTracker &&
      previousValues.statusTracker !== undefined &&
      this.hasStatusTrackerPreviousValues(projectSetup)
    ) {
      updatedDpr.dprStatusTracker = createEmptyDprStatusTracker(updatedDpr.id);
    }

    this.dprCarryOverFormulaCalculationService.applyPreviousValues(
      updatedDpr,
      previousValues
    );

    return updatedDpr;
  }

  /**
   * The previous values the DPR after the calculated one starts from. A section that
   * came without previous values keeps its stored carry-overs and running totals,
   * which an offline save of that section has dropped, so it is left out: the DPR
   * after it keeps the previous values it has for that section, or none, rather than
   * ones built from blanks.
   */
  private buildPreviousValues(
    projectSetup: ProjectSetup,
    calculatedDpr: DprDetails | null
  ): DprPreviousValuesSet {
    const previousValues =
      this.dprCarryOverFormulaCalculationService.buildPreviousValues(
        projectSetup,
        calculatedDpr
      );
    const sectionsWithoutHistory = getSectionsWithoutPreviousValues(
      calculatedDpr,
      projectSetup.statusTrackers,
      projectSetup.tableOfColumns
    );

    return {
      statusTracker: sectionsWithoutHistory.includesStatusTracker
        ? undefined
        : previousValues.statusTracker,
      tableOfColumns: new Map(
        [...previousValues.tableOfColumns].filter(
          ([tableOfColumnId]) =>
            !sectionsWithoutHistory.tableOfColumnIds.has(tableOfColumnId)
        )
      ),
    };
  }

  private warnLaterDprsNotUpdated(): void {
    this.toastNotificationService.warn(
      $localize`:@@dprDetails.dprAccumulationOfflineService.txtLaterDprsNotUpdated:Your changes were saved, but the carry-over and formula values of the later DPRs on this device couldn't be updated.`
    );
  }

  private hasPreviousValues(projectSetup: ProjectSetup): boolean {
    return (
      this.hasStatusTrackerPreviousValues(projectSetup) ||
      !!projectSetup?.tableOfColumns?.some(
        (tableOfColumn) =>
          getTableOfColumnsPreviousValueItemIds(tableOfColumn).length
      )
    );
  }

  /** Whether the DPR after this one reads the saved section. Unknown counts as yes. */
  private sectionHasPreviousValues(
    projectSetup: ProjectSetup,
    savedSection: DprAccumulationSection | undefined
  ): boolean {
    switch (savedSection?.kind) {
      case 'statusTracker':
        return this.hasStatusTrackerPreviousValues(projectSetup);
      case 'tableOfColumns': {
        const tableOfColumn = projectSetup?.tableOfColumns?.find(
          (x) => +x.id === +savedSection.tableOfColumnId
        );

        return (
          !!tableOfColumn &&
          !!getTableOfColumnsPreviousValueItemIds(tableOfColumn).length
        );
      }
      default:
        return true;
    }
  }

  private hasStatusTrackerPreviousValues(projectSetup: ProjectSetup): boolean {
    return !!projectSetup?.statusTrackers?.some(
      (statusTracker) =>
        getStatusTrackerPreviousValueColumnIds(statusTracker).length
    );
  }

  //#endregion
}
